/**
 * Convierte montos de UI/autofill a número de forma segura.
 * Soporta: 2950 | 2,950 | 2,950.50 | $ 2,950 | 2.950 (miles EU) | 2950,50
 * No trata la coma de miles como decimal (evita 2,950 → 2.95).
 */

const toFiniteOrNaN = (n) => (Number.isFinite(n) ? n : Number.NaN)

/** Quita moneda/texto; deja dígitos, coma, punto y signo. */
const stripCurrencyNoise = (value) =>
  String(value ?? '')
    .trim()
    .replace(/[^\d,.\-]/g, '')

const countOccurrences = (str, char) => str.split(char).length - 1

/**
 * Qué carácter es el separador decimal, o `null` si solo hay miles/ninguno.
 *
 * - Si hay `,` y `.`, gana el de más a la derecha (`1,234.56` vs `1.234,56`).
 * - Si solo hay uno: 3 dígitos después → miles (`2,950`); 1–2 → decimal (`2950.50`).
 * - Varios del mismo: el último es decimal solo si la fracción tiene 1–2 dígitos (`1.234.56`).
 */
const getDecimalSeparator = (raw) => {
  const lastComma = raw.lastIndexOf(',')
  const lastDot = raw.lastIndexOf('.')

  if (lastComma === -1 && lastDot === -1) return null

  if (lastComma !== -1 && lastDot !== -1) {
    return lastComma > lastDot ? ',' : '.'
  }

  const sep = lastComma !== -1 ? ',' : '.'
  const lastIndex = raw.lastIndexOf(sep)
  const fractionLength = raw.length - lastIndex - 1
  const occurrences = countOccurrences(raw, sep)

  if (occurrences > 1) {
    return fractionLength > 0 && fractionLength <= 2 ? sep : null
  }

  return fractionLength === 3 ? null : sep
}

/** Quita miles y deja un solo `.` decimal para `Number(...)`. */
const toPlainNumberString = (raw) => {
  const decimalSep = getDecimalSeparator(raw)

  if (!decimalSep) {
    return raw.replace(/[.,]/g, '')
  }

  const lastIndex = raw.lastIndexOf(decimalSep)
  const integerPart = raw.slice(0, lastIndex).replace(/[.,]/g, '')
  const fractionPart = raw.slice(lastIndex + 1).replace(/[.,]/g, '')

  return fractionPart ? `${integerPart}.${fractionPart}` : integerPart
}

export const parseAmount = (value) => {
  if (typeof value === 'number') return toFiniteOrNaN(value)

  const raw = stripCurrencyNoise(value)
  if (!raw || raw === '-' || raw === '.' || raw === ',') return Number.NaN

  return toFiniteOrNaN(Number(toPlainNumberString(raw)))
}
